import {
    Alert, Box, Button, Card, Chip, Link, Stack, Typography,
} from '@mui/joy';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

import AdCreativePreview from './AdCreativePreview';
import AdStatusChip from './AdStatusChip';
import { FORMAT_LABELS } from './adsConstants';
import AiStatusBadge from '../ui/AiStatusBadge';


/**
 * Объявление в очереди модерации: слева блок ровно в том виде,
 * в каком его увидит пользователь, справа поля и решение.
 *
 * Целевая ссылка выведена отдельной кликабельной строкой — модератор
 * обязан её открыть, битая ссылка одна из самых частых причин отказа.
 */
export default function AdModerationCreativeRow({
    creative, campaignName, advertiser, canModerate, busy, onApprove, onReject,
}) {
    return (
        <Card variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5}>
                <Box sx={{ flexShrink: 0 }}>
                    <AdCreativePreview creative={creative} format={creative.format} />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap">
                        <Typography level="title-sm">{creative.title}</Typography>
                        <AdStatusChip status={creative.moderation_status} kind="creative" />
                        <Chip size="sm" variant="outlined">
                            {FORMAT_LABELS[creative.format] || creative.format}
                        </Chip>
                        <Chip size="sm" variant="soft" color="neutral">
                            ID {creative.id}
                        </Chip>
                        <AiStatusBadge
                            status={creative.ai_status}
                            reason={creative.ai_reason}
                            confidence={creative.ai_confidence}
                        />
                    </Stack>

                    {(campaignName || advertiser) && (
                        <Typography level="body-xs" color="neutral" sx={{ mt: 0.4 }}>
                            {campaignName ? `Кампания: ${campaignName}` : ''}
                            {advertiser ? ` · Рекламодатель: ${advertiser}` : ''}
                        </Typography>
                    )}

                    <Box sx={{ mt: 1.2 }}>
                        <Typography level="body-sm">
                            <b>Текст:</b> {creative.text || '—'}
                        </Typography>
                        <Typography level="body-sm">
                            <b>Кнопка:</b> {creative.cta_text || 'Подробнее'}
                        </Typography>
                        <Typography level="body-sm">
                            <b>Рекламодатель в блоке:</b> {creative.brand_name || (
                                <Typography component="span" color="danger">не указан</Typography>
                            )}
                        </Typography>
                        {creative.landing_url && (
                            <Typography level="body-sm" sx={{ mt: 0.4 }}>
                                <b>Ссылка:</b>{' '}
                                <Link
                                    href={creative.landing_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    endDecorator={<OpenInNewRoundedIcon sx={{ fontSize: 14 }} />}
                                >
                                    {creative.landing_url}
                                </Link>
                            </Typography>
                        )}
                        {creative.video_url && (
                            <Typography level="body-sm" sx={{ mt: 0.4 }}>
                                <b>Видео:</b>{' '}
                                <Link href={creative.video_url} target="_blank" rel="noopener noreferrer">
                                    открыть ({creative.video_duration_sec || '?'} сек)
                                </Link>
                            </Typography>
                        )}
                        {creative.ord_token && (
                            <Typography level="body-xs" color="neutral" sx={{ mt: 0.4 }}>
                                Токен ОРД: {creative.ord_token}
                            </Typography>
                        )}
                    </Box>

                    {!creative.brand_name && (
                        <Alert color="warning" size="sm" sx={{ mt: 1 }}>
                            Не указано название рекламодателя — по закону о рекламе
                            блок нельзя показывать
                        </Alert>
                    )}

                    {creative.moderation_status === 'rejected' && creative.moderation_comment && (
                        <Alert color="danger" size="sm" sx={{ mt: 1 }}>
                            {creative.rejection_reason_display}: {creative.moderation_comment}
                        </Alert>
                    )}

                    {canModerate && creative.moderation_status !== 'approved' && (
                        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                            <Button
                                size="sm"
                                color="success"
                                startDecorator={<CheckRoundedIcon />}
                                loading={busy === `approve-${creative.id}`}
                                onClick={() => onApprove(creative)}
                            >
                                Одобрить
                            </Button>
                            <Button
                                size="sm"
                                color="danger"
                                variant="soft"
                                startDecorator={<BlockRoundedIcon />}
                                loading={busy === `reject-${creative.id}`}
                                onClick={() => onReject(creative)}
                            >
                                Отклонить
                            </Button>
                        </Stack>
                    )}

                    {canModerate && creative.moderation_status === 'approved' && (
                        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                            <Button
                                size="sm"
                                color="danger"
                                variant="plain"
                                startDecorator={<BlockRoundedIcon />}
                                loading={busy === `reject-${creative.id}`}
                                onClick={() => onReject(creative)}
                            >
                                Отозвать одобрение
                            </Button>
                        </Stack>
                    )}
                </Box>
            </Stack>
        </Card>
    );
}